import { type CstNodeLocation } from 'chevrotain';
import {
  type ConnectorCstNode,
  type DiagramCstNode,
  type DiagramTypeCstNode,
  type LabelCstNode,
  type NodeCstNode,
  type PropertiesCstNode,
  type PropertyCstNode,
  type StatementCstNode,
  type StepCstNode,
  type SubDiagramCstNode,
} from '../types';
import { CommonVisitor } from './CommonParser';
import {
  DiagramDatabase,
  type DiagramNode,
  type DiagramNodeId,
  type DiagramProperties,
} from './DiagramStructure';

// Possible connector end styles. Values match the whiteboards code.
// enum ConnectorEnd {
//   None = 0,
//   PointyArrow = 10,
//   FilledArrow = 20,
//   FilledCircle = 30,
// }

// Possible routing used for connectors.
// enum ConnectorRouter {
//   Straight = 0,
//   Bezier = 10,
//   OrthogonalAuto = 20,
//   OrthogonalManual = 30,
// }

// Traverse a tree of nodes from the syntax tree.
class CommonNodeGenerator extends CommonVisitor {
  // Diagram database currently being populated.
  diagramDatabase: DiagramDatabase | null = null;

  constructor() {
    super();
    this.validateVisitor();
  }

  diagram(ctx: DiagramCstNode): DiagramDatabase {
    const database = new DiagramDatabase();
    this.diagramDatabase = database;
    if (ctx.statement) {
      ctx.statement.forEach(statement => this.visit(statement));
    }
    database.buildHierarchy();
    this.diagramDatabase = null;

    return database;
  }

  statement(ctx: StatementCstNode) {
    const stmt = ctx.step || ctx.diagramType;

    if (stmt) {
      this.visit(stmt);
    }
  }

  diagramType(ctx: DiagramTypeCstNode) {
    this.diagramDatabase!.diagramType = ctx.DiagramType[0].image;
    this.diagramDatabase!.diagramTypeParseNode = ctx;

    this.diagramDatabase!.properties = this.optionalProperties(ctx);
    this.diagramDatabase!.propertiesParseNode = ctx.properties;
  }

  step(ctx: StepCstNode) {
    if (ctx.connector && ctx.ToNode) {
      const fromNode = this.visit(ctx.FromNode[0]);
      const toNode = this.visit(ctx.ToNode[0]);

      this.diagramDatabase!.createEdge(
        fromNode,
        toNode,
        this.visit(ctx.connector),
        this.location(ctx.connector[0].location)
      );
    } else {
      this.visit(ctx.FromNode[0]);
    }
  }

  subDiagram(ctx: SubDiagramCstNode) {
    const oldDatabase = this.diagramDatabase;
    const database = this.visit(ctx.diagram);
    this.diagramDatabase = oldDatabase;

    return database;
  }

  node(ctx: NodeCstNode): DiagramNodeId {
    let title: string;
    let database: DiagramDatabase | undefined;
    let location: CstNodeLocation;
    let instanceOf: DiagramNode | undefined;
    if (ctx.subDiagram) {
      database = this.visit(ctx.subDiagram);
      title = `Subdiagram ${database!.diagramType}`;
      location = this.location(ctx.subDiagram[0].location);
    } else if (ctx.Text) {
      title = ctx.Text[0].payload || '';
      location = this.location(ctx.Text[0]);
    } else {
      // Node is missing.
      title = ' ';
      location = this.location(undefined);
    }

    // See if this is a label reference.
    const nodeId = this.diagramDatabase!.findNodeByLabel(title);
    if (nodeId) {
      instanceOf = this.diagramDatabase!.node(nodeId);
    }

    // Create a database node.
    return this.diagramDatabase!.createNode({
      label: this.optionalLabel(ctx),
      title,
      properties: this.optionalProperties(ctx),
      location,
      database,
      instanceOf,
    });
  }

  connector(ctx: ConnectorCstNode): DiagramProperties {
    const props: DiagramProperties = {};
    const makeProp = (value: string | number, node: any) => ({
      value: value,
      keyLocation: this.location(node),
      valueLocation: this.location(node),
      parseNode: node,
    });

    // Extract connector direction information.
    if (ctx.ArrowLeft) {
      props.connectorFromStyle = makeProp('pointyArrow', ctx.ArrowLeft[0]);
      props.connectorToStyle = makeProp('none', ctx.ArrowLeft[0]);
    }
    if (ctx.ArrowRight) {
      props.connectorFromStyle = makeProp('none', ctx.ArrowRight[0]);
      props.connectorToStyle = makeProp('pointyArrow', ctx.ArrowRight[0]);
    }
    if (ctx.DashLong) {
      props.connectorFromStyle = makeProp('none', ctx.DashLong[0]);
      props.connectorToStyle = makeProp('none', ctx.DashLong[0]);
    }
    if (ctx.DoubleArrow) {
      props.connectorFromStyle = makeProp('pointyArrow', ctx.DoubleArrow[0]);
      props.connectorToStyle = makeProp('pointyArrow', ctx.DoubleArrow[0]);
    }

    if (ctx.ArrowLeftLong) {
      props.connectorFromStyle = makeProp('pointyArrow', ctx.ArrowLeftLong[0]);
      if (ctx.ArrowRightLong) {
        props.connectorToStyle = makeProp('pointyArrow', ctx.ArrowRightLong[0]);
      } else {
        props.connectorToStyle = makeProp('none', ctx.DashRight[0]);
      }
    } else if (ctx.DashLeft) {
      props.connectorFromStyle = makeProp('none', ctx.DashLeft[0]);
      if (ctx.ArrowRightLong) {
        props.connectorToStyle = makeProp('pointyArrow', ctx.ArrowRightLong[0]);
      } else {
        props.connectorToStyle = makeProp('none', ctx.DashRight[0]);
      }
    }

    return {
      ...props,
      ...this.optionalCaption(ctx),
      ...this.optionalProperties(ctx),
    };
  }

  properties(ctx: PropertiesCstNode): DiagramProperties {
    if (ctx.property) {
      return ctx.property.map(this.visit, this).reduce((acc, val) => {
        return { ...acc, ...val };
      }, {});
    } else {
      return {};
    }
  }

  property(ctx: PropertyCstNode): DiagramProperties {
    if (ctx.NameText && ctx.ValueText) {
      return {
        [ctx.NameText[0].payload!]: {
          value: ctx.ValueText[0].payload,
          keyLocation: this.location(ctx.NameText[0]),
          valueLocation: this.location(ctx.ValueText[0]),
          parseNode: ctx,
        },
      };
    } else {
      return {};
    }
  }

  label(ctx: LabelCstNode): string | undefined {
    if (ctx.Text?.[0]?.payload && ctx.Text[0].payload.length > 0) {
      return ctx.Text[0].payload;
    }
  }

  optionalCaption(ctx: NodeCstNode | ConnectorCstNode): DiagramProperties {
    if (ctx.Text?.[0]?.payload && ctx.Text[0].payload.length > 0) {
      return {
        caption: {
          value: ctx.Text[0].payload,
          keyLocation: this.location(ctx.Text[0]),
          valueLocation: this.location(ctx.Text[0]),
        },
      };
    } else {
      return {};
    }
  }

  // Handle optional properties.
  optionalProperties(
    ctx: NodeCstNode | DiagramTypeCstNode | ConnectorCstNode
  ): DiagramProperties {
    if (ctx.properties) {
      return this.visit(ctx.properties);
    } else {
      return {};
    }
  }

  optionalLabel(ctx: NodeCstNode): string | undefined {
    if (ctx.label) {
      return this.visit(ctx.label);
    }
  }

  // Information about where the node appears in the source.
  location(locationInfo?: CstNodeLocation): CstNodeLocation {
    if (!locationInfo) {
      return {
        startOffset: 0,
        endOffset: 0,
        startLine: 0,
        endLine: 0,
        startColumn: 1,
        endColumn: 1,
      };
    }
    return {
      startOffset: locationInfo.startOffset,
      endOffset: (locationInfo.endOffset ?? -1) + 1, // Not sure why the offset is always off by one.
      startLine: locationInfo.startLine,
      endLine: locationInfo.endLine,
      startColumn: locationInfo.startColumn,
      endColumn: (locationInfo.endColumn ?? -1) + 1,
    };
  }
}

const commonNodeGenerator = new CommonNodeGenerator();

export default commonNodeGenerator;
export { CommonNodeGenerator };
